import {
  GenericError,
  RedirectLoginOptions,
  useAuth0,
} from "@auth0/auth0-react";
import { ReactNode, useEffect } from "react";

import { Client, Middleware } from "@punto/api-client";

/**
 * `getAccessTokenSilently` errors that only a new login fixes — e.g. a refresh
 * token Auth0 dropped after 15 idle days (`invalid_grant`). With
 * `useRefreshTokensFallback` off, Auth0's documented handling is to call
 * `loginWithRedirect`: https://github.com/auth0/auth0-spa-js/blob/main/FAQ.md
 */
const LOGIN_REQUIRED_ERRORS: ReadonlySet<string> = new Set([
  "login_required",
  "missing_refresh_token",
  "invalid_grant",
]);

const getMiddleware = (
  getAccessTokenSilently: () => Promise<string>,
  loginWithRedirect: (options: RedirectLoginOptions) => Promise<void>,
  onError?: (error: Error) => void,
): Middleware => ({
  async onRequest({ request }) {
    try {
      const token = await getAccessTokenSilently();
      request.headers.set("Authorization", `Bearer ${token}`);

      return request;
    } catch (error) {
      console.error("Error getting token", error);
      onError?.(error as Error);

      if (
        error instanceof GenericError &&
        LOGIN_REQUIRED_ERRORS.has(error.error)
      ) {
        void loginWithRedirect({
          appState: {
            returnTo: `${window.location.pathname}${window.location.search}`,
          },
        });
      }

      // Without a token the API can only answer 401, which the app would read
      // as "no permissions" and answer with a logout (P-3142).
      throw error;
    }
  },
});

export const ApiAuthProvider = <T extends object>({
  children,
  fetchClient,
  renderLoading,
  onError,
}: {
  children?: ReactNode;
  fetchClient: Client<T, "application/json">;
  renderLoading?: ReactNode;
  onError?: (error: Error) => void;
}) => {
  const { getAccessTokenSilently, loginWithRedirect, isLoading } = useAuth0();

  useEffect(() => {
    const middleware = getMiddleware(
      getAccessTokenSilently,
      loginWithRedirect,
      onError,
    );
    fetchClient.use(middleware);

    return () => fetchClient.eject(middleware);
  }, [getAccessTokenSilently, loginWithRedirect, fetchClient, onError]);

  if (isLoading && renderLoading) {
    return renderLoading;
  }

  return <>{children}</>;
};
